import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useLocalStorageState } from 'hooks/useLocalStorageState';
import { useIsSidePanelOpen } from './side-panel-registry';

/** The desktop sidebar: the full list with labels, or the icon rail. Never hidden. */
export type SidebarMode = 'full' | 'rail';

/**
 * The reader's own choice. The key predates the rail, when false meant hidden; it now means the
 * rail, so a preference saved by the old shell carries over as the nearest thing it can be.
 */
export const SIDEBAR_PREFERENCE_KEY = 'pertento-sidebar-open';

type ResolveSidebarModeParams = {
  /** The stored preference: true is full, false the rail. */
  isFullPreferred: boolean;
  isSidePanelOpen: boolean;
  /** The reader expanded the nav during the chat that is open now. */
  isExpandedForChat: boolean;
};

/** A docked AI chat takes the room the labels would have; the reader can still take it back. */
export const resolveSidebarMode = ({
  isFullPreferred,
  isSidePanelOpen,
  isExpandedForChat,
}: ResolveSidebarModeParams): SidebarMode => {
  if (isSidePanelOpen) return isExpandedForChat ? 'full' : 'rail';
  return isFullPreferred ? 'full' : 'rail';
};

/**
 * The desktop sidebar's state. The preference is remembered; the fold for a chat is not, so
 * closing the chat returns the nav to whatever the reader had.
 *
 * Expanding the nav while a chat is open is a decision about this chat, so it holds until the side
 * panel closes or the page changes, and then resets: the next chat folds the nav again, and the
 * stored preference is never touched by a toggle made while a chat had the room. A new page is a
 * new chat even when the count never reaches zero, because one route's panel unmounts in the same
 * commit the next one's mounts.
 */
export const useSidebarMode = () => {
  const [isFullPreferred, setFullPreferred] = useLocalStorageState(SIDEBAR_PREFERENCE_KEY, true);
  const isSidePanelOpen = useIsSidePanelOpen();
  const { pathname } = useLocation();
  // The page the reader expanded the nav on. Compared during render, so a navigation folds the
  // nav in the same frame rather than one render later; the effect only tidies the state up.
  const [expandedOn, setExpandedOn] = useState<string | null>(null);
  const isExpandedForChat = expandedOn === pathname;

  useEffect(() => {
    if (!isSidePanelOpen || expandedOn !== pathname) setExpandedOn(null);
  }, [isSidePanelOpen, pathname]);

  const mode = resolveSidebarMode({ isFullPreferred, isSidePanelOpen, isExpandedForChat });

  const toggle = () =>
    isSidePanelOpen
      ? setExpandedOn((path) => (path === pathname ? null : pathname))
      : setFullPreferred((full) => !full);

  return { mode, isSidePanelOpen, toggle };
};
